import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
} from "react"
import { cn } from "../../lib/utils"

export interface PopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The trigger element (rendered inline). */
  trigger: ReactNode
  /** Popover content. */
  children: ReactNode
  /** Horizontal edge to anchor the content to. */
  align?: "start" | "end"
  /** Extra classes for the content panel (width, etc.). */
  contentClassName?: string
  /** Accessible role for the content panel. */
  role?: "menu" | "dialog" | "listbox"
  "aria-label"?: string
}

/**
 * Lightweight popover: opens upward from its trigger, animates in (Web
 * Animations API — no CSS-plugin dependency), and closes on outside click or
 * Escape. Non-modal; deliberately simple positioning tuned for bottom-anchored
 * controls (no collision flipping).
 */
export const Popover = ({
  open,
  onOpenChange,
  trigger,
  children,
  align = "start",
  contentClassName,
  role = "menu",
  "aria-label": ariaLabel,
}: PopoverProps) => {
  const wrapRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        onOpenChange(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false)
    }
    document.addEventListener("mousedown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open, onOpenChange])

  // Enter animation runs once when the popover opens — via an effect, not an
  // inline ref callback (which React re-invokes on every render, causing the
  // popover to re-animate/flicker on each interaction inside it).
  const contentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const node = contentRef.current
    if (!node) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return
    try {
      node.animate(
        [
          { opacity: 0, transform: "translateY(6px) scale(0.985)" },
          { opacity: 1, transform: "translateY(0) scale(1)" },
        ],
        { duration: 160, easing: "cubic-bezier(0,0,0.2,1)" },
      )
    } catch {
      /* WAAPI unavailable (for example, jsdom). */
    }
  }, [open])

  // Inject a click-to-toggle handler onto the trigger element, preserving any
  // handler it already has.
  const triggerElement = trigger as ReactElement<{
    onClick?: MouseEventHandler
  }>
  const triggerNode = isValidElement(trigger)
    ? cloneElement(triggerElement, {
        onClick: (event) => {
          triggerElement.props.onClick?.(event)
          onOpenChange(!open)
        },
      })
    : trigger

  return (
    <span ref={wrapRef} className="relative inline-flex">
      {triggerNode}
      {open && (
        <div
          ref={contentRef}
          role={role}
          aria-label={ariaLabel}
          className={cn(
            "absolute bottom-[calc(100%+8px)] z-50 origin-bottom overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-[0_12px_40px_-12px_rgba(0,0,0,0.18),0_2px_8px_-4px_rgba(0,0,0,0.08)] ring-1 ring-border/60 ring-offset-2 ring-offset-background",
            align === "end" ? "right-0" : "left-0",
            contentClassName,
          )}
        >
          {children}
        </div>
      )}
    </span>
  )
}
